import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppstoreOutlined, BookOutlined, SettingOutlined, DollarOutlined, DatabaseOutlined, TeamOutlined, LogoutOutlined, AccountBookOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { userSelector, increase } from "../store/user/slice";
import { useAppDispatch } from "../store/index";
import type { RootState } from "../store/index";
import { useSelector, useStore } from 'react-redux';
import { Button, Drawer, Dropdown, Menu, Modal, Tag, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AuthStore, signOut } from '../auth/auth-controller';
import { getToken } from '../utils/util';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { api_work_shift } from '../api/sale';
import { api_product } from '../api/product';
import { product } from '../model/master';
import { ALL_PERMISSION_IDS, CASHIER_DEFAULT_PERMISSION_IDS } from './employee/employee-detail';
import '../assets/css/stock-alert.css';

import { isPilotHost, isProductionApi } from '../api/api-config';

const logo = require('../assets/images/logo.png');
const logoDark = require('../assets/images/logo-dark.png');

const NavMenu: React.FC = (prop) => {

    const dispatch = useAppDispatch();
    const reduxStore = useStore<RootState>();
    const authStore: AuthStore = useMemo(
        () => ({ dispatch, getState: reduxStore.getState }),
        [dispatch, reduxStore]
    );
    const navigate = useNavigate();
    const user = useSelector(userSelector);
    const [current, setCurrent] = useState('');
    const [userName, setUserName] = useState<string>('');
    const [userPerms, setUserPerms] = useState<string[]>(ALL_PERMISSION_IDS);
    const [userRole, setUserRole] = useState<string>('manager');

    // Low Stock Alert Notification Center States
    const [lowStockProducts, setLowStockProducts] = useState<product[]>([]);
    const [lowStockTotalCount, setLowStockTotalCount] = useState(0);
    const [isAlertDrawerOpen, setIsAlertDrawerOpen] = useState(false);

    const fetchLowStock = async () => {
        try {
            const res = await api_product('LIST', { stock_filter: 'LOW', page_size: 100 });
            const list = Array.isArray(res) ? res : (res?.items || []);
            if (Array.isArray(list)) {
                // No client-side re-filter. `stock_filter: 'LOW'` is the
                // single definition and it now excludes products that have
                // never been received -- those are unfinished catalogue
                // import, not a reorder alert, and they were 2,400 of the
                // 2,584 this badge used to show.
                setLowStockProducts(list);
                const total = res?.pagination?.total !== undefined ? Number(res.pagination.total) : list.length;
                setLowStockTotalCount(total);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchLowStock();
        const timer = setInterval(fetchLowStock, 30000);
        return () => clearInterval(timer);
    }, []);

    // Sync current user and permissions.
    // Phase 7C-4: read from the Redux actor ONLY. The gate in App.tsx fills
    // it from SESSION_ME; `localStorage.uuid` is no longer read anywhere,
    // so a cached employee can never decide what this menu shows.
    // (Menu visibility is a convenience, not security -- Phase 7C-5.)
    useEffect(() => {
        // Every signed-in employee gets the full manager menu.
        //
        // Until now a `privilege = 2` (Sale) employee was shown the
        // cashier subset. That hid pages without protecting them: no route
        // in App.tsx is guarded and no Backend endpoint calls
        // `require_privilege`, so the same person reached any page by
        // typing its URL. The menu was the only thing pretending
        // otherwise, and a menu that hides what it cannot restrict is
        // worse than no menu filter at all -- it tells the shop a
        // separation exists that does not.
        //
        // The single exception is the in-app "สลับบทบาททดสอบสิทธิ์"
        // preview, which is the only thing that ever sets `permissions`
        // (SESSION_ME does not send it -- see `SessionActor`). Keeping
        // that branch keeps the preview able to demonstrate a restricted
        // menu without affecting anyone who actually logged in.
        //
        // Real per-page authorization stays a Backend question; see the
        // deferred 403 work in Phase 7C-5.
        let perms: string[] = ALL_PERMISSION_IDS;
        let role = 'manager';
        if (user && (user.name || user.code)) {
            setUserName(user.name || user.code || 'ผู้ใช้งาน');
            if (user.permissions && Array.isArray(user.permissions)) {
                perms = user.permissions;
                role = user.role || 'custom';
            }
        } else {
            setUserName('');
        }
        setUserPerms(perms);
        setUserRole(role);
    }, [user]);

    const canAccess = (permId: string) => {
        if (userRole === 'manager') return true;
        return userPerms.includes(permId);
    };

    // Dynamically filter menu items according to user permissions
    const menu: MenuProps['items'] = useMemo(() => {
        const items: MenuProps['items'] = [];

        // 1. Sale
        if (canAccess('sale')) {
            items.push({ label: 'ขาย', key: 'sale', icon: <DollarOutlined rev={undefined} /> });
        }

        // 2. Product Management
        const productChildren = [
            { label: 'สินค้า', key: 'product', perm: 'product' },
            { label: 'สั่งซื้อ (PO)', key: 'purchase_order', perm: 'purchase_order' },
            { label: 'รับเข้า', key: 'good_receive', perm: 'good_receive' },
            { label: 'สินค้าฝากขาย', key: 'consignment', perm: 'consignment' },
            { label: 'นับสต๊อก', key: 'check_stock', perm: 'check_stock' },
            { label: 'แยกแพค', key: 'separate', perm: 'separate' },
            { label: 'รวมแพค', key: 'include', perm: 'include' },
            { label: 'เบิกสินค้าบริการ', key: 'withdraw', perm: 'withdraw' },
        ].filter(item => canAccess(item.perm));

        if (productChildren.length > 0) {
            items.push({
                label: 'จัดการสินค้า',
                key: 'manage_product',
                icon: <DatabaseOutlined rev={undefined} />,
                children: productChildren.map(({ label, key }) => ({ label, key }))
            });
        }

        // 3. Member Management
        const memberChildren = [
            { label: 'สมาชิก', key: 'member', perm: 'member' },
            { label: 'กลุ่มสมาชิก', key: 'member_group', perm: 'member_group' },
        ].filter(item => canAccess(item.perm));

        if (memberChildren.length > 0) {
            items.push({
                label: 'จัดการสมาชิก',
                key: 'manage_member',
                icon: <TeamOutlined rev={undefined} />,
                children: memberChildren.map(({ label, key }) => ({ label, key }))
            });
        }

        // 4. Expense Management
        if (canAccess('expense')) {
            items.push({ label: 'ค่าใช้จ่าย', key: 'expense', icon: <AccountBookOutlined rev={undefined} /> });
        }

        // 5. Reports
        if (canAccess('report')) {
            items.push({
                label: 'รายงาน',
                key: 'manage_report',
                icon: <BookOutlined rev={undefined} />,
                children: [
                    { label: 'ภาพรวมสถิติ & กราฟ', key: 'report_analytics' },
                    { label: 'สรุปยอดขาย (วัน/สัปดาห์/เดือน)', key: 'report_sales' },
                    { label: 'ประวัติบิลขาย', key: 'report_receipts' },
                    { label: 'สรุปปิดกะการทำงาน', key: 'report_shifts' },
                    { label: 'สินค้าขายดี & สต๊อก', key: 'report_products' },
                ]
            });
        }

        // 5. Config
        const configChildren = [
            { label: 'ตั้งค่าทั่วไป', key: 'config_general', perm: 'config_general' },
            { label: 'ตั้งค่าการแยกแพค', key: 'config_separate', perm: 'config_separate' },
            { label: 'ประเภทสินค้า', key: 'product_type', perm: 'product_type' },
            { label: 'หน่วยสินค้า', key: 'product_unit', perm: 'product_unit' },
            { label: 'ประเภทการชำระเงิน', key: 'payment_type', perm: 'payment_type' },
            // Moved out of "อื่นๆ" on the user's instruction: managing staff
            // and their permissions is a setting, not a leftover.
            { label: 'พนักงาน', key: 'employee', perm: 'employee' },
        ].filter(item => canAccess(item.perm));

        if (configChildren.length > 0) {
            items.push({
                label: 'ตั้งค่า',
                key: 'config',
                icon: <SettingOutlined rev={undefined} />,
                children: configChildren.map(({ label, key }) => ({ label, key }))
            });
        }

        // 6. Other Master Data
        const otherChildren = [
            { label: 'พิมพ์บาร์โค้ด', key: 'barcode_print', perm: 'product' },
            { label: 'พิมพ์ราคาสินค้า', key: 'price_tag_print', perm: 'product' },
            { label: 'ผู้ขายส่งสินค้า', key: 'vendor', perm: 'vendor' },
        ].filter(item => canAccess(item.perm));

        if (otherChildren.length > 0) {
            items.push({
                label: 'อื่นๆ',
                key: 'other',
                icon: <AppstoreOutlined rev={undefined} />,
                children: otherChildren.map(({ label, key }) => ({ label, key }))
            });
        }

        return items;
    }, [userPerms, userRole]);

    // The role preview never reaches the live shop. It is a development
    // affordance: it dispatches a fabricated persona into the store so a
    // restricted menu can be demonstrated. Nothing it sets is written --
    // the session token is untouched and the Backend stamps the actor from
    // that verified session -- but on the real system it still advertises a
    // separation that does not exist, and it puts an invented name in front
    // of whoever is standing at the till. Gated on which Backend this build
    // talks to rather than on the hostname, so localhost, the LAN and the
    // Pilot all keep it and only jindamarket.com loses it.
    const rolePreviewItems: MenuProps['items'] = isProductionApi() ? [] : [
        {
            type: 'group',
            label: '🧪 สลับบทบาทเพื่อทดสอบสิทธิ์',
            children: [
                { label: '👑 ผู้จัดการร้าน (Full Admin - สิทธิ์ครบ)', key: 'switch_manager' },
                { label: '👤 แคชเชียร์ (Cashier - สิทธิ์หน้าร้าน)', key: 'switch_cashier' },
                { label: '📦 คลังสินค้า (Inventory Staff)', key: 'switch_inventory' },
            ]
        },
        { type: 'divider' },
    ];

    const userDropdownItems: MenuProps['items'] = [
        ...rolePreviewItems,
        { label: 'ออกจากระบบ', key: 'logout', icon: <LogoutOutlined rev={undefined} /> },
    ];

    const onClick: MenuProps['onClick'] = (e) => {
        setCurrent(e.key);
        switch (e.key) {
            case 'sale':
                navigate("/sale");
                break;
            case 'product':
                navigate("/product");
                break;
            case 'barcode_print':
                navigate("/product/barcode-print");
                break;
            case 'price_tag_print':
                navigate("/product/price-tag-print");
                break;
            case 'purchase_order':
                navigate("/purchase/order");
                break;
            case 'good_receive':
                navigate("/good/receive");
                break;
            case 'consignment':
                navigate("/consignment");
                break;
            case 'check_stock':
                navigate("/check/stock");
                break;
            case 'separate':
                navigate("/separate");
                break;
            case 'include':
                navigate("/include");
                break;
            case 'config_separate':
                navigate("/config/separate");
                break;
            case 'employee':
                navigate("/employee");
                break;
            case 'vendor':
                navigate("/vendor");
                break;
            case 'withdraw':
                navigate("/withdraw");
                break;
            case 'member':
                navigate("/member");
                break;
            case 'member_group':
                navigate("/member/group");
                break;
            case 'config_general':
                navigate("/config/general");
                break;
            case 'product_type':
                navigate("/product/type");
                break;
            case 'product_unit':
                navigate("/product/unit");
                break;
            case 'payment_type':
                navigate("/payment/type");
                break;
            case 'expense':
                navigate("/expense");
                break;
            case 'report':
            case 'manage_report':
            case 'report_analytics':
                navigate("/report?tab=analytics");
                break;
            case 'report_sales':
                navigate("/report?tab=sales");
                break;
            case 'report_receipts':
                navigate("/report?tab=receipts");
                break;
            case 'report_shifts':
                navigate("/report?tab=shifts");
                break;
            case 'report_products':
                navigate("/report?tab=products");
                break;

            default:
                break;
        }
    };

    const getConfigModal = (titile: string, content: string) => {
        return {
            title: titile,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        }
    }

    const handleSwitchTestRole = (targetRole: 'manager' | 'cashier' | 'inventory') => {
        let testUser: any;
        if (targetRole === 'manager') {
            testUser = {
                id: 1,
                name: 'ผู้จัดการ ร้านค้า (Admin)',
                code: 'MGR01',
                privilege: 1,
                privilege_name: 'ผู้จัดการ (สิทธิ์เต็ม)',
                role: 'manager',
                permissions: ALL_PERMISSION_IDS,
                default_page: 'sale'
            };
        } else if (targetRole === 'cashier') {
            testUser = {
                id: 2,
                name: 'พนักงาน แคชเชียร์ (Cashier)',
                code: 'CSH01',
                privilege: 2,
                privilege_name: 'แคชเชียร์หน้าร้าน',
                role: 'cashier',
                permissions: CASHIER_DEFAULT_PERMISSION_IDS,
                default_page: 'sale'
            };
        } else if (targetRole === 'inventory') {
            testUser = {
                id: 3,
                name: 'พนักงาน คลังสินค้า (Stock)',
                code: 'STK01',
                privilege: 2,
                privilege_name: 'ฝ่ายคลังสินค้า',
                role: 'inventory',
                permissions: ['product', 'check_stock', 'good_receive', 'separate', 'include', 'withdraw'],
                default_page: 'product'
            };
        }
        // Phase 7C-4: in-memory preview only. Nothing is written to
        // localStorage; a refresh restores the real actor from SESSION_ME.
        dispatch(increase(testUser));
        Modal.success({
            ...getConfigModal('สลับบทบาททดสอบสิทธิ์สำเร็จ', `ขณะนี้ระบบเปลี่ยนเป็นบทบาท "${testUser.name}" เรียบร้อยแล้ว (แถบเมนูด้านบนจะแสดงเฉพาะหน้าที่ได้รับสิทธิ์ทันที)`),
            ...{
                onOk() {
                    navigate(`/${testUser.default_page}`);
                }
            }
        });
    };

    const onMenuUserClick: MenuProps['onClick'] = async (key) => {
        if (key.key === "logout") {
            checkWorkShift();
        } else if (key.key === 'switch_manager') {
            handleSwitchTestRole('manager');
        } else if (key.key === 'switch_cashier') {
            handleSwitchTestRole('cashier');
        } else if (key.key === 'switch_inventory') {
            handleSwitchTestRole('inventory');
        }
    };

    const logout = async () => {
        // Phase 7C-4: SESSION_REVOKE with the bearer token, then the local
        // session is cleared in every case -- a user must always be able
        // to sign out of the till in front of them. No employee id or
        // `key-id` is sent: the token alone says whose session this is.
        const result = await signOut(authStore);
        if (!result.revokedRemotely) {
            message.warning('ออกจากระบบในเครื่องนี้แล้ว แต่ไม่สามารถยืนยันการยกเลิกเซสชันกับเซิร์ฟเวอร์ได้ เซสชันเดิมจะหมดอายุเองตามกำหนด');
        }
        navigate(`/login`);
    }

    const checkWorkShift = async () => {
        let token = '';
        await getToken().then((item: string) => {
            token = item;
        });
        let getWorkShift: any = await api_work_shift('GET_BY_CONDITION', {"device": token});
        if (getWorkShift !== null && getWorkShift !== undefined) {
            if (getWorkShift['id'] > 0) {
                if (getWorkShift['status'] === 1) {
                    Modal.confirm({
                        ...getConfigModal('แจ้งเตือน', 'ยังไม่มีการปิดกะการทำงาน คุณต้องการออกจากระบบหรือไม่ ?'),
                        ...{ async onOk() { logout() }}
                    });
                } else {
                    logout();
                }
            } else {
                logout();
            }
        } else {
            logout();
        }
    }

    const [theme, setTheme] = useState<string>(() => {
        return window.localStorage.getItem('theme') || 'light';
    });

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        window.localStorage.setItem('theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'dark' ? 'light' : 'dark');
    };

    useEffect(() => {
        setCurrent(window.location.pathname.replaceAll('/', '_').replace('_', ''));
    }, []);

    return (
        <>
            <img src={theme === 'dark' ? logoDark : logo} alt="logo" className='logo' />
            {/* Shown ONLY on pilot.jindamarket.com. The Pilot looks exactly
                like the real thing and talks to the Staging database, so
                whoever is using it has to be able to tell at a glance. */}
            {isPilotHost() && (
                <span
                    className='pilot-badge'
                    data-testid='pilot-environment-badge'
                    title='ระบบทดสอบ (Pilot) — ข้อมูลแยกจากระบบจริง'
                >
                    PILOT / STAGING • REMOTE TEST
                </span>
            )}
            <div className='nav-box'>
                <Menu theme="dark" onClick={onClick} selectedKeys={[current]} mode="horizontal" items={menu} />
            </div>
            <div className='nav-right-actions'>
                {/* Low Stock Alert Bell Button */}
                <button 
                    type="button" 
                    className={`nav-stock-alert-btn ${(lowStockTotalCount > 0 || lowStockProducts.length > 0) ? 'has-alert' : ''}`}
                    onClick={() => setIsAlertDrawerOpen(true)}
                    title={`การแจ้งเตือน: สินค้าใกล้หมด ${lowStockTotalCount || lowStockProducts.length} รายการ`}
                    aria-label="Low stock notifications"
                    data-testid="btn-stock-alert-bell"
                >
                    <FontAwesomeIcon icon="bell" />
                    {(lowStockTotalCount > 0 || lowStockProducts.length > 0) && (
                        <span className="nav-stock-alert-badge" data-testid="badge-stock-alert-count">
                            {(lowStockTotalCount || lowStockProducts.length) > 99 ? '99+' : (lowStockTotalCount || lowStockProducts.length)}
                        </span>
                    )}
                </button>

                <button 
                    type="button" 
                    className='theme-toggle-btn' 
                    onClick={toggleTheme}
                    title={theme === 'dark' ? 'สลับเป็นโหมดสว่าง (Light Mode)' : 'สลับเป็นโหมดมืด (Dark Mode)'}
                    aria-label="Toggle theme mode"
                >
                    <span className="theme-toggle-icon">{theme === 'dark' ? '☀️' : '🌙'}</span>
                    <span className="theme-toggle-text">{theme === 'dark' ? 'โหมดสว่าง' : 'โหมดมืด'}</span>
                </button>
                <Dropdown menu={{ items: userDropdownItems, onClick: onMenuUserClick }} autoAdjustOverflow={false} placement={'bottomRight'}>
                    <div className='user-nav-profile' title={`ผู้ใช้งาน: ${userName || 'ผู้ใช้งาน'}`}>
                        <FontAwesomeIcon icon="user" className='userIcon' />
                        <span className='user-nav-name'>{userName || 'ผู้ใช้งาน'}</span>
                    </div>
                </Dropdown>
            </div>

            {/* Low Stock Notification Center Drawer */}
            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: 700 }}>
                        <span>🔔</span>
                        <span>ศูนย์แจ้งเตือนสินค้าใกล้หมดสต๊อก</span>
                        {lowStockProducts.length > 0 && (
                            <Tag color="red" style={{ borderRadius: '8px', fontWeight: 700, marginLeft: 'auto' }}>
                                {lowStockProducts.length} รายการ
                            </Tag>
                        )}
                    </div>
                }
                placement="right"
                onClose={() => setIsAlertDrawerOpen(false)}
                open={isAlertDrawerOpen}
                width={440}
                data-testid="drawer-stock-alert"
            >
                <div className="stock-alert-drawer-header">
                    <span className="alert-icon">⚠️</span>
                    <div>
                        <div className="alert-title">สินค้าต่ำกว่าเกณฑ์ขั้นต่ำ ({lowStockProducts.length} รายการ)</div>
                        <div className="alert-subtitle">ควรสั่งซื้อสินค้าเติมสต๊อกเพื่อไม่ให้สินค้าขาดขาย</div>
                    </div>
                </div>

                {lowStockProducts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748B' }}>
                        <div style={{ fontSize: '40px', marginBottom: '10px' }}>✅</div>
                        <div style={{ fontWeight: 700, fontSize: '15px' }}>สินค้าทุกรายการสต๊อกเพียงพอ</div>
                        <div style={{ fontSize: '13px', marginTop: '4px' }}>ไม่มีสินค้าที่ต่ำกว่าเกณฑ์ขั้นต่ำ</div>
                    </div>
                ) : (
                    <div className="stock-alert-list">
                        {lowStockProducts.map(p => (
                            <div key={p.id} className="stock-alert-item-card critical">
                                <div className="stock-alert-item-info">
                                    <span className="stock-alert-item-code">{p.product_code}</span>
                                    <span className="stock-alert-item-name">{p.product_name}</span>
                                    {p.vendor_name && (
                                        <span className="stock-alert-item-vendor">🏢 {p.vendor_name}</span>
                                    )}
                                </div>
                                <div className="stock-alert-item-qty">
                                    <span className="stock-alert-qty-badge">
                                        เหลือ {p.quantity || 0}
                                    </span>
                                    <span className="stock-alert-qty-limit">
                                        เกณฑ์: {p.limit_quantity || 10} {p.product_unit_name || 'ชิ้น'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="stock-alert-drawer-actions">
                    <Button
                        type="primary"
                        icon={<FontAwesomeIcon icon="bolt" />}
                        style={{
                            height: '42px',
                            borderRadius: '12px',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #FFA07A 0%, #FF6B4A 100%)',
                            border: 'none'
                        }}
                        onClick={() => {
                            setIsAlertDrawerOpen(false);
                            navigate('/purchase/order');
                        }}
                        data-testid="btn-drawer-go-po"
                    >
                        ⚡ สร้างใบสั่งซื้อสินค้า (PO)
                    </Button>
                    <Button
                        type="default"
                        icon={<FontAwesomeIcon icon="boxes-stacked" />}
                        style={{ height: '42px', borderRadius: '12px', fontWeight: 600 }}
                        onClick={() => {
                            setIsAlertDrawerOpen(false);
                            navigate('/product');
                        }}
                        data-testid="btn-drawer-go-product"
                    >
                        📦 ไปที่หน้ารายการสินค้า
                    </Button>
                </div>
            </Drawer>
        </>
    );
}

export default NavMenu;
